import { motion } from "framer-motion";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { EOY_RECAP_VIEW_TYPES } from "@circle-react/apps/EoyRecapApp/constants";
import {
  TAB_CONFIG,
  navVariants,
  containerVariants,
  tabVariants,
  activeTabSpring,
} from "./constants";
import { useNavigationScroll } from "./hooks/useNavigationScroll";

const i18nRoot = "eoy_recap_2025.navigation_tabs";

export interface TabConfig {
  id: string;
  labelKey: string;
  sectionId: string;
}

export interface NavigationTabsProps {
  scrollContext?: "admin" | "non-admin";
}

export const NavigationTabs = ({
  scrollContext = EOY_RECAP_VIEW_TYPES.NON_ADMIN,
}: NavigationTabsProps) => {
  const { selectedTab, isScrolled, scrollToSection } = useNavigationScroll({
    tabConfig: TAB_CONFIG,
    scrollContext,
  });

  return (
    <motion.div
      className="fixed z-20 mx-auto mt-2 px-4 py-2 left-1/2 -translate-x-1/2 rounded-2xl backdrop-blur-xl border border-solid shadow-lg shadow-black/5 overflow-x-auto md:overflow-x-visible scrollbar-hide max-w-[calc(100vw-2rem)] md:max-w-none"
      role="tablist"
      aria-label={t([i18nRoot, "aria_label"])}
      variants={navVariants}
      initial="default"
      animate={isScrolled ? "scrolled" : "default"}
    >
      <motion.div
        className="flex items-center justify-center gap-2 flex-nowrap min-w-max"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {TAB_CONFIG.map((config, index) => (
          <motion.button
            key={config.id}
            id={config.id}
            className={classNames(
              "relative px-5 py-3 rounded-full text-sm font-medium transition-colors",
              {
                "text-white": index === selectedTab,
                "text-white/70 hover:text-white focus-visible:text-white":
                  index !== selectedTab,
              },
            )}
            onClick={() => scrollToSection(index)}
            variants={tabVariants}
            whileHover={{ scale: 1.05 }}
            whileFocus={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            tabIndex={index === selectedTab ? 0 : -1}
            aria-selected={index === selectedTab}
            aria-controls={config.sectionId}
            role="tab"
          >
            {index === selectedTab && (
              <motion.div
                layoutId="activeTab"
                className="absolute inset-0 bg-white/10 border border-[#7079B4] rounded-full"
                transition={activeTabSpring}
              />
            )}
            <span className="relative z-10">
              {t([i18nRoot, config.labelKey])}
            </span>
          </motion.button>
        ))}
      </motion.div>
    </motion.div>
  );
};
